import { useMemo, useState } from 'react';
import sortBy from 'lodash/sortBy';

import { FileDropperContextProvider } from 'lib/form/file-dropper/file-dropper';
import { SubActivityEdit } from '../sub-activity/sub-activity-edit';
import MultipleChoiceEditOption from './multiple-choice-edit-option';
import { EditProps } from './multiple-choice.constants';
import { MultipleChoiceItems } from './multiple-choice.style';
import { useActivityBuilderContext } from 'approot/activity-builder/activity-builder.context';
import { getComponentSelectorId } from '../shared/logic/get-component-selector-id';
import { isSingleSelect } from './logic/is-single-select';
import { LANGUAGE_CODE_EN_AU } from 'global/constants';

const MultipleChoiceEdit = ({
  properties,
  sectionId,
  id,
  type,
  sort,
  subActivityProperties,
  suggestedAnswerProperties,
  version,
}: EditProps) => {
  const [isUpdating, setIsUpdating] = useState<boolean>(false);

  const {
    multipleChoice: { options, answerMode, isFreeAnswerMultiSelect },
  } = properties;

  const sortedOptions = useMemo(() => sortBy(options, 'sort'), [options]);
  const isOneOptionCorrect = useMemo(
    () =>
      isSingleSelect({
        answerMode,
        isFreeAnswerMultiSelect,
        options,
      }),
    [options, answerMode, isFreeAnswerMultiSelect]
  );

  const { isZoomToFit } = useActivityBuilderContext();

  return (
    <SubActivityEdit
      id={id}
      component={{
        id,
        type,
        sort,
        properties,
        sectionId,
        __typename: 'ActivitiesMultipleChoiceComponent',
        language: LANGUAGE_CODE_EN_AU,
        version,
      }}
      subActivityDetails={subActivityProperties}
    >
      {() => (
        <MultipleChoiceItems
          id={getComponentSelectorId(id)}
          $display={properties.layout?.display}
          $isResponsive={!isZoomToFit}
          role={isOneOptionCorrect ? 'radiogroup' : 'checkbox'}
        >
          {sortedOptions.map((option, index) => {
            return (
              <MultipleChoiceEditOption
                key={option.itemId}
                option={option}
                index={index}
                isOneOptionCorrect={isOneOptionCorrect}
                properties={properties}
                subActivityProperties={subActivityProperties}
                suggestedAnswerProperties={suggestedAnswerProperties}
                id={id}
                sort={sort}
                type={type}
                sectionId={sectionId}
                isUpdating={isUpdating}
                setIsUpdating={state => setIsUpdating(state)}
                language={LANGUAGE_CODE_EN_AU}
                version={version}
              />
            );
          })}
        </MultipleChoiceItems>
      )}
    </SubActivityEdit>
  );
};

const MultipleChoiceEditWrapper = (props: EditProps) => (
  <FileDropperContextProvider>
    <MultipleChoiceEdit {...props} />
  </FileDropperContextProvider>
);

export default MultipleChoiceEditWrapper;
